package app.nestbridge.feature.main.ui

import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.Chat
import androidx.compose.material.icons.automirrored.outlined.ShowChart
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.outlined.People
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.feature.children.screen.ChildrenScreen
import app.nestbridge.feature.home.screen.HomeScreen
import app.nestbridge.feature.main.navigation.TabHost
import app.nestbridge.feature.main.screen.MainShellEvent
import app.nestbridge.feature.main.screen.MainShellScreen
import app.nestbridge.feature.main.screen.MainShellState
import app.nestbridge.feature.main.screen.MainTab
import app.nestbridge.feature.messages.screen.MessagesScreen
import app.nestbridge.feature.progress.screen.ProgressScreen
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.runtime.screen.Screen
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

typealias MainShellContent = @Composable (MainTab, Modifier) -> Unit

@CircuitInject(
    screen = MainShellScreen::class,
    scope = AppScope::class,
)
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MainShellUi(
    state: MainShellState,
    modifier: Modifier = Modifier,
) {
    MainShellUiContent(state = state, modifier = modifier)
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MainShellUiContent(
    state: MainShellState,
    modifier: Modifier = Modifier,
    content: MainShellContent = { tab, mod ->
        TabHost(root = tab.screen(), modifier = mod) { }
    },
) {
    val mainShellBar = MainShellBar()

    Scaffold(
        modifier = Modifier
            .fillMaxSize()
            .windowInsetsPadding(WindowInsets.safeDrawing),
        topBar = {
            mainShellBar.TopBar(
                title = state.selectedTab.label(),
                profilePictureUrl = state.profilePictureUrl,
                userName = state.userName,
                onProfileClick = {
                    state.eventSink(MainShellEvent.ProfileClicked)
                },
            )
        },
        bottomBar = {
            mainShellBar.BottomBar(
                selectedTab = state.selectedTab,
                onTabSelected = { tab ->
                    state.eventSink(MainShellEvent.TabSelected(tab))
                }
            )
        },
    ) { padding ->
        content(state.selectedTab, Modifier.padding(padding))
    }
}

fun MainTab.screen(): Screen = when (this) {
    MainTab.HOME -> HomeScreen
    MainTab.CHILDREN -> ChildrenScreen
    MainTab.MESSAGES -> MessagesScreen
    MainTab.PROGRESS -> ProgressScreen
}

fun MainTab.icon(): ImageVector = when (this) {
    MainTab.HOME -> Icons.Filled.Home
    MainTab.CHILDREN -> Icons.Outlined.People
    MainTab.MESSAGES -> Icons.AutoMirrored.Outlined.Chat
    MainTab.PROGRESS -> Icons.AutoMirrored.Outlined.ShowChart
}

fun MainTab.label(): String = when (this) {
    MainTab.HOME -> "Home"
    MainTab.CHILDREN -> "Children"
    MainTab.MESSAGES -> "Messages"
    MainTab.PROGRESS -> "Progress"
}

// region Previews

@Preview
@Composable
private fun MainShellUiPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        MainShellUiContent(
            state = MainShellState(
                selectedTab = MainTab.HOME,
                eventSink = {},
            ),
            content = { tab, _ -> Text("Preview: ${tab.label()}") },
        )
    }
}

// endregion
